import type { EnvMatrix, EnvMatrixIssue } from "@/lib/api";
import type { CellRef } from "./MatrixGrid";
import { TINT } from "./tints";

const KIND = {
  config: { chip: "Setup", chipCls: "bg-bg-secondary text-text-secondary", edge: "border-l-text-muted" },
  rule: { chip: "Rule broken", chipCls: `${TINT.danger} text-danger`, edge: "border-l-danger" },
  notice: { chip: "Notice", chipCls: `${TINT.warning} text-warning`, edge: "border-l-warning" },
} as const;

const SECTIONS = [
  { kind: "config", title: "Setup" },
  { kind: "rule", title: "Broken rules" },
  { kind: "notice", title: "Notices" },
] as const;

/**
 * The issues, for the Issues modal, in sections: setup problems, broken
 * rules, then notices. Passing checks fold away underneath.
 */
export function IssuesList({ m, onShow }: { m: EnvMatrix; onShow: (at: CellRef) => void }) {
  return (
    <div className="space-y-5">
      {m.issues.length === 0 && (
        <p className="text-sm text-success">
          Every rule passes and every key is declared in render.yaml.
        </p>
      )}

      {SECTIONS.map((s) => {
        const items = m.issues.filter((i) => i.kind === s.kind);
        if (items.length === 0) return null;
        return (
          <section key={s.kind}>
            <h3 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
              {s.title} · {items.length}
            </h3>
            <div className="grid items-start gap-3 md:grid-cols-2">
              {items.map((i) => (
                <IssueCard key={i.id} issue={i} onShow={onShow} />
              ))}
            </div>
          </section>
        );
      })}

      {m.passing.length > 0 && (
        <details>
          <summary className="cursor-pointer text-sm text-text-muted">
            {m.passing.length} {m.passing.length === 1 ? "check" : "checks"} passing
          </summary>
          <ul className="mt-2 grid gap-1.5 text-sm md:grid-cols-2">
            {m.passing.map((c, idx) => (
              <li key={idx} className="flex gap-2">
                <i
                  className={`fa-solid ${c.status === "pass" ? "fa-check text-success" : "fa-minus text-text-muted"} mt-1 text-[11px]`}
                />
                <span>
                  <span className="text-text-heading">{c.label}</span>
                  {c.detail && <span className="block text-xs text-text-muted">{c.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function IssueCard({ issue, onShow }: { issue: EnvMatrixIssue; onShow: (at: CellRef) => void }) {
  const k = KIND[issue.kind];
  // A notice covering several keys on one service lists them as chips, each
  // one a way into the table; a single key keeps the plain link.
  const many = issue.kind === "notice" && issue.cells.length > 1;
  const first = issue.cells[0];
  return (
    <article className={`space-y-1.5 rounded-token-md border border-border border-l-[3px] bg-bg-card p-3 text-sm ${k.edge}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded px-1.5 py-0.5 font-mono text-[10.5px] font-medium uppercase tracking-wide ${k.chipCls}`}>
          {issue.kind === "notice" ? issue.title : k.chip}
        </span>
        {!many &&
          issue.keys.slice(0, 2).map((key) => (
            <code key={key} className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-heading">
              {key}
            </code>
          ))}
      </div>
      {issue.kind !== "notice" && <p className="font-semibold text-text-heading">{issue.title}</p>}
      {issue.detail && <p className="text-text">{issue.detail}</p>}
      {many && (
        <div className="flex flex-wrap gap-1.5">
          {issue.cells.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => onShow({ serviceId: c.serviceId, key: c.key, kind: "var" })}
              title="Show in table"
              className="rounded bg-bg-secondary px-1.5 py-0.5 font-mono text-xs text-text-heading transition-colors hover:bg-accent-muted hover:text-accent"
            >
              {c.key}
            </button>
          ))}
        </div>
      )}
      {issue.fix && (
        <p className="text-xs text-text-secondary">
          <span className="font-semibold text-text">Fix: </span>
          {issue.fix}
        </p>
      )}
      {issue.why && <p className="text-xs text-text-muted">{issue.why}</p>}
      {first && !many && (
        <button
          type="button"
          onClick={() => onShow({ serviceId: first.serviceId, key: first.key, kind: "var" })}
          className="pt-0.5 text-xs font-medium text-accent hover:underline"
        >
          Show in table
        </button>
      )}
    </article>
  );
}
